function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import * as React from 'react';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';
import clsx from 'clsx';
import { Section, SectionItem } from 'common/components/items-menu/types';
import { DisplaySize, Placement } from 'common/components/tooltip/types';
import { TRACKING_EVENTS as QueryBlockTrackingEvent } from 'common/query-builder/query-block/constants';
import { MATH_TYPE, MetricType, PRIMITIVE_MATH_TYPES, TopLevelMetricType } from 'common/types/reports/bookmark';
import { EventItem } from 'common/query-builder/query-block/events-menu/types';
import { ALL_USERS_COHORT, BEHAVIOR_MULTI_ATTRIBUTION } from 'common/query-builder/query-block/properties-menu/constants';
import { getMathTypeFromEntry, getMultiAttributionFromEntry, isSupportedMultiAttributionTypeForEntry, queryBuilderUtils } from 'common/query-builder/query-builder/util';
import { ProjectFeatureFlag, ReportName, ResourceType, SPECIAL_EVENTS } from 'common/report/constants';
import { dataGroupsClient } from 'common/report/queries/app-api/data-groups-client';
import { lookupTablesStore } from 'common/report/stores/lookup-tables';
import { TrackingUsageContext } from 'common/report/tracking-setup';
import { QueryReason } from 'common/report/util/query-tracking/types';
import { getAlphaLabelFromIndex, RollingWindowSize } from 'common/util';
import { insertAtIndex, removeByIndex, replaceByIndex } from 'common/util/array';
import { emitTrackEvent, eventPathContainsSelectors, preventTracking, propagateEvent as _propagateEvent } from 'common/util/dom';
import { getActiveElement } from 'common/util/focusable';
import { withStyles } from 'common/util/react';
import { assertUnreachable } from 'common/util/typescript';
import { captureWorkflowTiming, QueryStartEvent, WorkflowComplete } from 'common/util/workflow-timing';
import { DEFAULT_DATA_GROUP } from 'common/widgets/data-group-selector';
import { DataGroup } from 'common/widgets/data-group-selector/types';
import { DateRangeAttributes } from 'common/widgets/date-range-picker/models/date-range';
import { DateRangeFactory } from 'common/widgets/date-range-picker/models/date-range-factory';
import { DisplayContext } from 'common/widgets/date-range-picker/models/types';
import { PropertyFilter } from 'common/widgets/property-filter-menu/models/property-filter';
import { createFirstTimeEventFilter, isFirstTimeEventFilter } from 'common/widgets/property-filter-menu/models/utils';
import { StickyPlaceholderReact } from 'common/widgets/sticky-placeholder';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { useQueryContextManager } from 'common/contexts/react/query-context-manager';
import { useMetrics } from 'common/contexts/react/metrics-resource-context';
import { useBehaviors } from 'common/contexts/react/behaviors-context';
import { useDragAndDropManager } from 'common/contexts/react/drag-and-drop-manager-context';
import { IronContextRegistry } from 'common/report/context-registry';
import { useTranslation } from 'common/i18n/hooks';
import { IncludeCustomPropertyType } from 'common/query-builder/query-block/properties-menu/types';
import { generateFilterTrackingProps } from '../query-block/filter-menu/util';
import { convertDataGroupIdToSet } from '../query-block/utils/data-group-util';
import { SelectorTypeAggregationLabel } from '../types/shared-types';
import { MenuType } from '../query-block/types';
import { MenuType as MultiMetricsMenuType, MultiMetricsSectionItem } from '../query-block/multi-metrics-menu/types';
import { isEventSegment, Segment } from '../query-block/segment-menu/types';
import { AggregationsMappings, commonAddEntryTooltipAttrs, ControlBarAction, ControlBarActionItem, ControlBarActionItems, ControlBarActionValue, NESTED_AGGREGATIONS_MENU_ITEMS, TutorialTooltips } from './constants';
import css from './index.inline.styl';
import { EntryRowProps } from './row-types/types';
import { QueryEntrySectionTrackingEvent, ResourceTrackingEvent, ResourceTrackingTypes } from './tracking';
import { Action, DROP_POSITION, EMPTY_EVENT_ENTRY, EMPTY_EXCLUSION_EVENT_ENTRY, Entry, EntryItem, EntryPart, EntryType, FilteringCapabilities, GroupEventBehaviors, isEventBasedEntry, isFormulaEntry, isInlineCohortItem, isMetricEntry, isPropertyEntry, isPropertySelectorType, isQueryMultiMetricEntry, isQuerySuperEntry, isSavedMetricEntry, isSegmentEntry, isUserBasedEntry, QueryBehaviorEntry, QueryEntrySectionProps, QueryEventEntry, QueryMetricEntry, QueryPropertyEntry, QuerySuperEntry, RowAddress, RowLabelType, RowTypeComplexOption, RowTypeOption, SavedCohortItem, SectionOpenEntry, SelectorType, SelectorTypeAggregation, SelectorTypeDefinitions, SelectorTypesLabels, StagingRow, UpdateStatus } from './types';
// eslint-disable-next-line import/no-cycle
import { getEntryRowHelpers } from './row-types';
import { getBehaviorEntryTrackingProps, isAggregateUserFreqByGroupSegmentation, isMultiAttributionBehavior, isMultiAttributionSegmentEntry } from './row-types/behavior/utils';
import { hasFirstTimeEventFilter, isComputedListPropertyEntry, promptRename } from './row-types/row-util';
import { getNewEmptyEntry } from './row-types/util';
import { formatDataGroupLabel, getSelectedAggregationData, getSelectedGroupLabel } from './util';
import { addEntryFromExternalSection, calculateFormulaReferenceChanges, segmentToFilter, updateFormulaReferences } from './util/entry-transforms';
import { AddEntryRow } from './components/add-row';
import { AddEntryHeader } from './components/add-row-from-header';
interface QueryEntrySectionState {
  openEntry: SectionOpenEntry;
  dataGroups: DataGroup[];
  draggingStarted: boolean;
  draggingGripperIndex: number;
  draggingOverStepIndex: number;
  dropPosition: DROP_POSITION;
  hasRowWithHoveredContextMenu: boolean;
  placeholders: Record<number, [EntryPart, Entry]>;
  stagedRows: Entry[];
  stagedRow: Entry;
  isTypeSelectOpen: boolean;
  hasOpenDropdown: boolean;
  hasOpenEntrySelectorDropdown: boolean;
  hasOpenMetricUnitSelectorDropdown: boolean;
}

// Defines types of entities that can be added to the list
type TypeList = { [key in SelectorType]?: SectionItem<RowTypeComplexOption[`value`]> };
const LIST_TYPES: TypeList = {
  [ResourceType.Cohort]: {
    label: _ref => {
      let {
        t
      } = _ref;
      return t("query-builder.metrics.cohort");
    },
    value: ResourceType.Cohort,
    icon: "community",
    tooltip: {
      text: _ref2 => {
        let {
          t
        } = _ref2;
        return t("query-builder.metrics.cohort-tooltip");
      },
      'max-width': "250px"
    }
  },
  [ResourceType.Event]: {
    label: _ref3 => {
      let {
        t
      } = _ref3;
      return t("query-builder.metrics.event");
    },
    value: ResourceType.Event,
    icon: "event",
    tooltip: {
      text: _ref4 => {
        let {
          t
        } = _ref4;
        return t("query-builder.metrics.event-tooltip");
      },
      'max-width': "250px"
    }
  },
  [ResourceType.User]: {
    label: _ref5 => {
      let {
        t
      } = _ref5;
      return t("query-builder.metrics.profile");
    },
    value: ResourceType.User,
    icon: "profile",
    tooltip: {
      text: _ref6 => {
        let {
          t
        } = _ref6;
        return t("query-builder.metrics.profile-tooltip");
      },
      'max-width': "250px"
    }
  },
  [ResourceType.Formula]: {
    label: _ref7 => {
      let {
        t
      } = _ref7;
      return t("query-builder.metrics.formula");
    },
    value: ResourceType.Formula,
    icon: "formula",
    tooltip: {
      text: _ref8 => {
        let {
          t
        } = _ref8;
        return t("query-builder.metrics.formula-tooltip");
      },
      'max-width': "250px"
    }
  },
  [MetricType.Funnel]: {
    label: _ref9 => {
      let {
        t
      } = _ref9;
      return t("query-builder.metrics.funnel");
    },
    value: MetricType.Funnel,
    icon: "funnels-tab",
    tooltip: {
      text: _ref10 => {
        let {
          t
        } = _ref10;
        return t("query-builder.metrics.funnel-tooltip");
      },
      'max-width': "250px"
    }
  },
  [MetricType.Retention]: {
    label: _ref11 => {
      let {
        t
      } = _ref11;
      return t("query-builder.metrics.retention");
    },
    value: MetricType.Retention,
    icon: "retention-tab",
    tooltip: {
      text: _ref12 => {
        let {
          t
        } = _ref12;
        return t("query-builder.metrics.retention-tooltip");
      },
      'max-width': "250px"
    }
  },
  [MetricType.RetentionFrequency]: {
    label: _ref13 => {
      let {
        t
      } = _ref13;
      return t("query-builder.metrics.retention");
    },
    value: MetricType.RetentionFrequency,
    icon: "retention-tab"
  }
};
const queryStartedWorkflowComplete: WorkflowComplete<QueryEntrySection> = _ref14 => {
  let {
    detail
  } = _ref14;
  return detail.eventName === QueryStartEvent;
};
export class QueryEntrySection extends React.PureComponent<Pick<IronContextRegistry, `dragAndDropManager` | `queryContextManager` | `currentProject` | `metrics` | `behaviors` | `i18n`> & QueryEntrySectionProps, QueryEntrySectionState> {
  constructor() {
    super(...arguments);
    _defineProperty(this, "nextEntryKey", 1);
    _defineProperty(this, "isConnected", false);
    _defineProperty(this, "rootEl", React.createRef<HTMLDivElement>());
    _defineProperty(this, "headerEl", React.createRef<HTMLDivElement>());
    _defineProperty(this, "_onDisconnected", []);
    /**
     * When context menu is open, `draggable=true` prevents users from selecting text
     * within the context panel. We temporarily set `draggable=false` when child context
     * menu is hovered
     */
    _defineProperty(this, "onMouseEnterContextMenu", () => this.setState({
      hasRowWithHoveredContextMenu: true
    }));
    _defineProperty(this, "onMouseLeaveContextMenu", () => this.setState({
      hasRowWithHoveredContextMenu: false
    }));
    _defineProperty(this, "state", {
      openEntry: {},
      dataGroups: [DEFAULT_DATA_GROUP],
      draggingStarted: false,
      draggingGripperIndex: null,
      draggingOverStepIndex: null,
      dropPosition: null,
      hasRowWithHoveredContextMenu: false,
      placeholders: {},
      stagedRows: [],
      stagedRow: null,
      isTypeSelectOpen: false,
      hasOpenDropdown: false,
      hasOpenEntrySelectorDropdown: false,
      hasOpenMetricUnitSelectorDropdown: false
    });
    _defineProperty(this, "lookupTablesStoreCallback", _ref15 => {
      let {
        fromUpdate
      } = _ref15;
      if (fromUpdate) {
        // only refresh on manual update (a temp mapping is registered / unregistered)
        this.forceUpdate();
      }
    });
  }
  // this is to support the workflow timers
  public onDisconnected(fn: () => void) {
    this._onDisconnected.push(fn);
  }
  componentDidMount() {
    this.isConnected = true;
    this.updateStagedRowsFromEntries();
    this.fetchDataGroups();
    lookupTablesStore.subscribe(this.lookupTablesStoreCallback);
    if (this.props.defaultEntry) {
      this.updateEntries(Action.Add, {
        index: 0,
        entry: this.props.defaultEntry
      });
    }
    if (this.props.stagingRow) {
      this.setStagingRow(this.props.stagingRow);
    }
    this.rootEl.current.addEventListener("track", this.props.onTrack);
    this.rootEl.current.addEventListener("savedCustomEvent", this.props.onSavedCustomEvent);
    this.rootEl.current.addEventListener("mouseEnterContextMenu", this.onMouseEnterContextMenu);
    this.rootEl.current.addEventListener("mouseLeaveContextMenu", this.onMouseLeaveContextMenu);
  }
  componentWillUnmount() {
    this.isConnected = false;
    this.props.dragAndDropManager.removeDragoverEl(this.rootEl.current);
    lookupTablesStore.unsubscribe(this.lookupTablesStoreCallback);
    this.rootEl.current.removeEventListener("track", this.props.onTrack);
    this.rootEl.current.removeEventListener("savedCustomEvent", this.props.onSavedCustomEvent);
    this.rootEl.current.removeEventListener("mouseEnterContextMenu", this.onMouseEnterContextMenu);
    this.rootEl.current.removeEventListener("mouseLeaveContextMenu", this.onMouseLeaveContextMenu);
    this._onDisconnected.forEach(fn => fn());
  }
  componentDidUpdate(prevProps: QueryEntrySectionProps, prevState: QueryEntrySectionState) {
    if (!isEqual(this.props.rowTypes, prevProps.rowTypes)) {
      this.abortRowStage();
    }
    if (!isEqual(this.props.entries, prevProps.entries) || !isEqual(this.props.minEntries, prevProps.minEntries)) {
      this.updateStagedRowsFromEntries();
    }
    if (!isEqual(this.props.projectId, prevProps.projectId) || !isEqual(this.props.workspaceId, prevProps.workspaceId)) {
      this.fetchDataGroups();
    }

    // sync staging row
    if (!isEqual(this.props.stagingRow, prevProps.stagingRow)) {
      this.setStagingRow(this.props.stagingRow);
    }

    // if the user clicks out of an entry value without clicking an option, abandon it
    if (!this.state.openEntry.part && prevState.openEntry.part === EntryPart.Value && this.state.stagedRow && !this.isEntryIntermediatePlaceholder(this.state.stagedRow)) {
      this.abortRowStage();
    }
  }

  // todo: move this down
  private hasBehavioralFrequencyFilter(entry: QueryMetricEntry) {
    var _entry$event;
    return this.props.behaviorMode && !isFormulaEntry(entry) && !isSavedMetricEntry(entry) && ((_entry$event = entry.event) === null || _entry$event === void 0 ? void 0 : _entry$event.value) === ALL_USERS_COHORT.value;
  }

  // todo: move this out
  private getNestedSectionParams(): Partial<QueryEntrySectionProps> {
    return {
      allowCohortFilter: this.props.allowCohortFilter,
      dataGroupsEnabled: this.props.dataGroupsEnabled,
      reportName: this.props.reportName,
      dataGroupId: this.props.dataGroupId,
      canCustomizeNumericBuckets: this.props.canCustomizeNumericBuckets,
      canRenameStep: this.props.canRenameStep,
      disableFirstTimeEventFilters: this.props.disableFirstTimeEventFilters,
      enableEnhancedListFilters: this.props.enableEnhancedListFilters,
      enableWorkspaceFilterRestrictions: this.props.enableWorkspaceFilterRestrictions,
      excludeAutotrackEvents: this.props.excludeAutotrackEvents,
      excludeCustomEvents: this.props.excludeCustomEvents,
      excludeDimensionProperties: this.props.excludeDimensionProperties,
      includeCustomProperties: this.props.includeCustomProperties,
      showBorrowedProperties: this.props.showBorrowedProperties,
      isBlockedPeople: this.props.isBlockedPeople,
      maxDataHistoryDays: this.props.maxDataHistoryDays,
      otherFilterOptions: this.props.otherFilterOptions,
      readonly: this.props.readonly,
      showAllEvents: this.props.showAllEvents,
      showAnyEvent: this.props.showAnyEvent,
      hideEditControls: this.props.hideEditControls,
      showSessions: this.props.showSessions,
      showTopEvents: this.props.showTopEvents,
      showSpecialDatasetProperty: this.props.showSpecialDatasetProperty,
      supportB2bCohorts: this.props.supportB2bCohorts,
      workspaceId: this.props.workspaceId,
      userId: this.props.userId,
      projectId: this.props.projectId,
      projectAsOfDate: this.props.projectAsOfDate,
      projectHasPeople: this.props.projectHasPeople,
      projectHasCohorts: this.props.projectHasCohorts,
      trackName: this.props.trackName,
      hideEvents: this.props.hideEvents
    };
  }

  /**
   * Starts a drag operation only when it initiates from a gripper control,
   * this is achieved by setting the event index when a gripper is pressed
   */
  private onEntryStepDragStart(ev: React.DragEvent, fromIndex: number): void {
    ev.stopPropagation();
    this.setState(state => {
      var _getActiveElement;
      if (state.draggingGripperIndex !== fromIndex) {
        ev.preventDefault();
        return null;
      }
      (_getActiveElement = getActiveElement() as HTMLElement) === null || _getActiveElement === void 0 || _getActiveElement.blur();
      const draggingEntry = this.getEntry(state.draggingGripperIndex);

      // only should be blank in safari tests
      if (ev.dataTransfer) {
        this.writeRowAddress(ev, fromIndex);
        ev.dataTransfer.effectAllowed = "copyMove";
        ev.dataTransfer.dropEffect = ev.altKey && this.canDuplicateEntry(draggingEntry) ? "copy" : "move";
      }

      // Fire a custom event so all the section rerender accordingly (greying out if not a valid drop area)
      this.props.dragAndDropManager.setDraggingInfo(this, cloneDeep(draggingEntry));
      return {
        draggingStarted: true
      };
    });
  }
  private onEntryStepDragEnter(ev: React.DragEvent, index: number) {
    ev.preventDefault();
    ev.stopPropagation();
    this.setState({
      draggingOverStepIndex: index
    });
  }
  private onEntryStepDragLeave() {
    this.props.dragAndDropManager.removeDragoverEl(this.rootEl.current);
    this.setState({
      draggingOverStepIndex: null
    });
  }
  private onEntryStepDragOver(ev: DragEvent | React.DragEvent, index: number) {
    ev.preventDefault();
    this.props.dragAndDropManager.addDragoverEl(this.rootEl.current);
    const draggingEntry = this.getEntry(this.state.draggingGripperIndex);
    ev.dataTransfer.dropEffect = ev.altKey && this.canDuplicateEntry(draggingEntry) ? "copy" : "move";
    this.setState({
      draggingOverStepIndex: index,
      dropPosition: this.getDropPosition(ev, index)
    });
  }

  // todo: public because its called by drag and drop utils. audit this
  public onEntryStepDragClear() {
    this.props.dragAndDropManager.clearDraggingInfo();
    this.setState({
      draggingStarted: false,
      draggingGripperIndex: null,
      draggingOverStepIndex: null,
      dropPosition: null
    });
  }
  private onEntryStepDrop(ev: DragEvent | React.DragEvent, dropIndex: number) {
    ev.preventDefault();
    ev.stopPropagation();
    const draggingEntry = this.props.dragAndDropManager.draggingEntry;
    const shouldClone = ev.altKey || "inheritedFromBoard" in draggingEntry && draggingEntry.inheritedFromBoard;
    const fromAddress = ev.dataTransfer ? this.readRowAddress(ev) : [-1] as RowAddress;

    // Insert after the drop target index if dropping below (assuming we have at least one entry, otherwise it should stay 0)
    // Techincially this calls the transform twice TODO bucc think of how to make more efficient w/o relying on dragover
    if (this.getDropPosition(ev, dropIndex) === DROP_POSITION.BELOW && this.getEntries().length) {
      dropIndex++;
    }
    const immovableEntries = this.getEntries(true).slice(Math.max(dropIndex - 1, 0), dropIndex + 1);
    const dropTargetIsInherited = immovableEntries.length > 0 && "inheritedFromBoard" in immovableEntries[0] && immovableEntries[0].inheritedFromBoard;
    const immovableEntryKeys = immovableEntries.map(entry => entry.entryKey);
    if (!shouldClone && (
    // Don't do anything if self drop
    this.props.dragAndDropManager.draggingEl === this && immovableEntryKeys.includes(draggingEntry.entryKey) ||
    // Don't allow drops on inherited entries
    dropTargetIsInherited)) {
      this.onEntryStepDragClear();
      return;
    }
    this.handleEntryStepDrop(fromAddress, dropIndex, shouldClone);
    this.onEntryStepDragClear();
  }
  private getDefaultDateValue() {
    return this.props.dateRange || DateRangeFactory.createDateRange({}, {
      displayContext: this.props.timeDisplayContext
    }).getAttributes();
  }

  // todo: this can be a util
  private isDateRangeType(type: SelectorType) {
    var _this$getAddListTypeI;
    return [SelectorTypeDefinitions.Behavior, SelectorTypeDefinitions.EventLaunch].includes(type as SelectorTypeDefinitions) || ((_this$getAddListTypeI = this.getAddListTypeItems()[type]) === null || _this$getAddListTypeI === void 0 ? void 0 : _this$getAddListTypeI.includeDateRange);
  }

  // TODO: There's tracking split among this function and in updateEntries. Merge the tracking
  // logic to avoid confusion.
  // todo: audit this
  private trackEntrySelectorEvent(ev: CustomEvent, index: number) {
    preventTracking(ev);

    // This is a hack due to (what appears to be) a race condition surfaced in our WCT tests.
    // I spent a few hours trying to identify root cause but the system is sufficiently complex
    // enough and I'm sufficiently ignorant enough that I couldn't figure it out. The bug manifests itself
    // as this.getEntries() is an empty list.
    if (this.getEntries(true).length <= index) {
      return;
    }
    const type = this.getEntry(index).type;
    // TODO(CC): Track metric entry selector event
    if (type === TopLevelMetricType.MetricEntry) return;
    const commonProps = {
      'Entry index': index,
      'Entry type': type
    };
    const {
      eventName,
      eventPrefix,
      props
    } = ev.detail;
    if (eventName === "".concat(eventPrefix).concat(QueryBlockTrackingEvent.HOVER_CONTEXT_PANEL)) {
      ev.stopPropagation();
      const evtName = ev.detail.eventName.replace(eventPrefix, "");
      this.dispatchTrack(evtName, _objectSpread(_objectSpread({}, props), commonProps));
      return;
    }
    if (!ev.cancelable || isPropertySelectorType(type)) {
      // Filter and segment actions are tracked elsewhere
      return;
    }
    let trackingAction = null;
    switch (eventName) {
      case "[mp-query-builder] Add property filter":
        trackingAction = Action.AddFilter;
        break;
      case "[mp-query-builder] Edit property filter":
        trackingAction = Action.EditFilter;
        break;
      case "[mp-query-builder] Remove property filter":
        trackingAction = Action.RemoveFilter;
        break;
      case "[mp-query-builder] Select date range":
        trackingAction = Action.SelectDateRange;
        break;
      case "[mp-query-builder] Select Event":
      case "[mp-query-builder] Select event":
      case "[mp-query-builder] Select Property":
      case "[mp-query-builder] Select segment":
        trackingAction = Action.Select;
        break;
      default:
        throw new Error("tracking not defined for entry selector event: ".concat(eventName));
    }
    this.dispatchTrack(this.getTrackingEvent(type, trackingAction), _objectSpread(_objectSpread({}, props), commonProps));
  }

  /**
   * Brings focus to the first placeholder row.
   * @param force - Activates even if autoactivatePlaceholders prop is false
   */
  private autoactivatePlaceholders() {
    let force = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    if (!force && !this.props.autoactivatePlaceholders) {
      return;
    }
    if (this.state.stagedRow && isMetricEntry(this.state.stagedRow)) {
      return;
    }
    const firstPlaceholder = this.state.stagedRows.find(row => this.isEntryPlaceholder(row));
    if (firstPlaceholder) {
      const activePart = this.getEntryDefaultActiveEntryPart(firstPlaceholder);
      if (activePart) {
        this.setState({
          openEntry: {
            index: this.getEntryIndex(firstPlaceholder),
            part: activePart
          }
        });
      }
    }
  }

  /**
   * Sets the component to a row staging mode and optionally sets additional entry fields
   */
  private setStagingRow(partialStagedRow: StagingRow) {
    if (partialStagedRow) {
      const lastStagedRow = this.state.stagedRows[this.state.stagedRows.length - 1];
      if (lastStagedRow && this.isEntryPlaceholder(lastStagedRow)) {
        // This branch is needed for list-of-objects breakdown -> drag to filter section. Before this change a second filter
        // row was added (and the second row's property-menu was eager opened) when the first row was missing a filter value
      } else {
        const index = this.state.stagedRows.length;
        const type = Object.values(this.getAddListTypeItems())[0].value;
        const entry = _objectSpread(_objectSpread({}, this.getNewEntry(type, {})), typeof partialStagedRow === "boolean" ? {} : partialStagedRow) as Entry;

        // avoid auto opening the first staged row
        this.setState({
          stagedRow: entry
        });
        const activePart = this.getEntryDefaultActiveEntryPart(entry, index);
        if (activePart) {
          this.setState({
            openEntry: {
              index,
              part: activePart
            }
          });
        }
      }
    }
  }
  private showPlaceholderForEntry(part: EntryPart, index: number, entry?: Entry) {
    this.setState(state => ({
      placeholders: _objectSpread(_objectSpread({}, state.placeholders), {}, {
        [index]: [part, entry]
      })
    }));
  }

  /**
   * Gets the type of rows that can be dropped into this section.
   */
  // todo: public because it's used by drag drop util
  public getAllowedDropRowTypes(): RowTypeOption[] {
    const rowTypes = this.props.rowTypes;
    if (rowTypes.length === 1 && rowTypes[0] === SelectorTypeDefinitions.Metrics) {
      return [ResourceType.Event, ResourceType.User, ResourceType.Formula, MetricType.Funnel, MetricType.Retention, ResourceType.Cohort];
    }
    const groupedEventDropAllowed = this.props.groupEventBehavior === GroupEventBehaviors.Nest;
    return [...rowTypes, ...(groupedEventDropAllowed ? [SelectorTypeDefinitions.GroupedEvent] : [])];
  }

  /** Disables adding until last row is commited */
  private isAddingDisabled() {
    return this.getEntries(true).some(entry => this.isEntryPlaceholder(entry)) || this.props.disabled || this.canAddRow() === false;
  }

  /**
   * Checks if any row can be duplicated.
   */
  private isDuplicateSupported() {
    return !this.props.disableCloneEntry && this.canAddRow();
  }

  /**
   * Determines the allowed drop areas for an entry row.
   */
  private getEntryDropAreas(index: number): DROP_POSITION[] {
    const dropAreas = [];
    if (!this.props.rowDropDisabled && this.props.dragAndDropManager.draggingEntry) {
      if (Object.keys(this.state.placeholders).length > 0 && this.state.placeholders[index]) {
        return [];
      }
      const result = addEntryFromExternalSection(this, this.props.dragAndDropManager.draggingEl, this.props.dragAndDropManager.draggingEntry, index, this.nextEntryKey);
      const {
        modifyExisting,
        entry,
        entries
      } = result !== null && result !== void 0 ? result : {};
      if (entries && this.props.rowLimit && entries.length > this.props.rowLimit) {
        // if the length of entries is greater than row limit, return empty drop areas
        return [];
      } else if (modifyExisting) {
        // if a drop on this index would modify it, only allow "ON" type drops
        return [DROP_POSITION.ON];
      } else if (entry !== undefined) {
        // if the result on this index is a placeholder or the section is empty, only allow "HEADER" drop
        // else it can be place on either "ABOVE" or "BELOW" side
        if (this.props.dragAndDropManager.draggingEl !== this && this.isEntryPlaceholder(entry, index) || !this.getEntries().length) {
          return [DROP_POSITION.HEADER];
        }
        dropAreas.push(DROP_POSITION.ABOVE);
        dropAreas.push(DROP_POSITION.BELOW);
      }
    }
    return dropAreas;
  }

  /**
   * Determines if entries are deletable.
   */
  private canDeleteEntry(): boolean {
    const hasSingleListOfObjectsFilter = !!this.props.listProperty && this.getEntries(true).length === 1;
    return !this.props.disableDeleteEntry && !hasSingleListOfObjectsFilter;
  }

  /**
   * Determines if an entry can be dragged.
   */
  private canDragEntry(index: number): boolean {
    const hasNewQBUI = this.hasNewQBUI();
    const entry = this.getEntry(index);
    const isDraggingAllowed = this.props.rowDraggable && this.props.rowLimit !== 1 && !("undraggable" in entry && entry.undraggable) && !this.props.disabled;
    if (isMetricEntry(entry) || isQueryMultiMetricEntry(entry)) {
      if (this.props.reportName !== ReportName.Insights) {
        return false;
      }
      return !this.state.hasOpenDropdown && isDraggingAllowed;
    }
    const dragEnabled = !isFirstTimeEventFilter(entry.event as PropertyFilter) && !this.state.hasOpenDropdown && isDraggingAllowed;
    return hasNewQBUI ? dragEnabled : dragEnabled && !this.isAddingDisabled();
  }

  /**
   * Determines if an entry can be duplicated.
   */
  private canDuplicateEntry(entry: Entry): boolean {
    return this.isDuplicateSupported() &&
    // todo(micah): move this either to row type helpers OR inside their renderers if possible
    !isMultiAttributionSegmentEntry(entry) && !isComputedListPropertyEntry(entry);
  }

  /**
   * Gets common entry overflow controls.
   */
  private getCommonOverflowControls(entry: Entry): ControlBarActionItem[] {
    if (isMetricEntry(entry) || !(entry !== null && entry !== void 0 && entry.event)) return [];
    const index = this.getEntryIndex(entry);
    const isPlaceholder = this.isEntryPlaceholder(entry, index);
    const eventValue = (entry as QueryEventEntry).event.value;
    const isTopEvents = eventValue === SPECIAL_EVENTS.TOP_EVENTS;
    const filteringCapabilities = this.getRowFilteringCapabilities(entry);
    const items: ControlBarActionItem[] = [
    // filter
    filteringCapabilities.canApplyFilter && !this.hasBehavioralFrequencyFilter(entry as QueryMetricEntry) && ControlBarActionItems[ControlBarAction.Filter],
    // event 1st time filters
    filteringCapabilities.canApplyFirstTimeEventFilter && _objectSpread(_objectSpread({}, ControlBarActionItems[ControlBarAction.FirstTimeFilter]), filteringCapabilities.firstTimeFilterDisabled ? {
      tooltip: {
        text: "To apply 1st time filter, select a different event"
      },
      isDisabled: true
    } : {}), this.canAddMultiAttribution(entry) && ControlBarActionItems[ControlBarAction.AddMultiAttribution],
    // rename metric (e.g. event, cohort, formula)
    !isPlaceholder && !isInlineCohortItem(entry.event as SavedCohortItem) && this.props.canRenameStep && !isTopEvents && ControlBarActionItems[ControlBarAction.Rename],
    // delete row
    this.hasNewQBUI() && this.canDeleteEntry() && ControlBarActionItems[ControlBarAction.Delete],
    // duplicate row
    !isPlaceholder && this.canDuplicateEntry(entry) && ControlBarActionItems[ControlBarAction.Duplicate]].filter(Boolean);
    return items;
  }

  // todo: public because its used by entity transforms. move this out
  public getEntryLabel(index: number): string {
    switch (this.props.rowLabelType) {
      case RowLabelType.Alpha:
        return getAlphaLabelFromIndex(index);
      case RowLabelType.Numeric:
        return "".concat(index + 1);
      default:
        return "-";
    }
  }

  /**
   * Handles common entry actions.
   */
  private async onEntryAction(value: ControlBarActionValue, index: number) {
    const currentEntry = this.getEntry(index);
    switch (value.action) {
      case ControlBarAction.Filter:
        return this.showPlaceholderForEntry(EntryPart.Filters, index);
      case ControlBarAction.FirstTimeFilter:
        {
          var _currentEntry$filters;
          const firstTimeEventFilter = createFirstTimeEventFilter();
          // TODO(CC): Apply first time event filter once metric entry is ready
          if (isMetricEntry(currentEntry) || isQueryMultiMetricEntry(currentEntry)) {
            return;
          }
          const entry: QuerySuperEntry = _objectSpread(_objectSpread({}, currentEntry), {}, {
            filters: [...((_currentEntry$filters = currentEntry.filters) !== null && _currentEntry$filters !== void 0 ? _currentEntry$filters : []), firstTimeEventFilter]
          });
          this.updateEntries(Action.Edit, {
            entry,
            index
          });
          this.trackAction(index, entry, Action.AddFilter, generateFilterTrackingProps(firstTimeEventFilter));
          return;
        }
      case ControlBarAction.Rename:
        return promptRename(this.getEntryProps(currentEntry, index), this.props.i18n);
      case ControlBarAction.Duplicate:
        return this.updateEntries(Action.Clone, {
          index
        });
      case ControlBarAction.Delete:
        return this.removeEntry(index);
      case ControlBarAction.AddMultiAttribution:
        return this.setState({
          openEntry: {
            index,
            part: EntryPart.MultiAttribution
          }
        });
      default:
        assertUnreachable(value as never);
    }
  }

  /** Check to see if we can add a row */
  private canAddRow() {
    if (this.props.readonly || this.props.disableAddEntryReason) {
      return false;
    }
    const rowLimit = this.props.rowLimit;
    return rowLimit > 0 ? this.getEntries(true).length < rowLimit : true;
  }

  /** Construct a row address for this index */
  private getRowAddress(index: number): RowAddress {
    const rowAddr = [index];
    if (Number.isInteger(this.props.rowIndex)) {
      rowAddr.unshift(this.props.rowIndex);
    }
    return rowAddr as RowAddress;
  }

  /** Write index as the row address into ev.dataTransfer */
  private writeRowAddress(ev: DragEvent | React.DragEvent, index: number): void {
    ev.dataTransfer.setData("application/json", JSON.stringify(this.getRowAddress(index)));
  }

  /** Read a row address off of a DragEvent dataTransfer */
  private readRowAddress(ev: DragEvent | React.DragEvent): RowAddress {
    return JSON.parse(ev.dataTransfer.getData("application/json")) as RowAddress;
  }
  private mapEntriesToStagedRows(entries: Entry[]): Entry[] {
    const {
      print
    } = this.props.i18n;
    return entries.map((entry, index) => {
      let entryKey: number;
      if (index < this.state.stagedRows.length) {
        var _this$state$stagedRow;
        // q(micah): why would we ever want to re-use entry keys?
        entryKey = (_this$state$stagedRow = this.state.stagedRows[index].entryKey) !== null && _this$state$stagedRow !== void 0 ? _this$state$stagedRow : this.nextEntryKey++;
      } else {
        entryKey = this.nextEntryKey++;
      }
      if (isMetricEntry(entry)) {
        return _objectSpread(_objectSpread({}, entry), {}, {
          entryKey
        });
      }
      const type = entry.type || ResourceType.Event;
      let aggregationOperator = getMathTypeFromEntry(entry);
      // If the current entry is empty, set the default operator to unique in count menu
      if (this.isEmptyPlaceholder(entry, index) && this.hasNewQBUI()) {
        aggregationOperator = PRIMITIVE_MATH_TYPES.UNIQUE;
      }
      return _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, entry), aggregationOperator && {
        aggregationOperator
      }), {}, {
        type,
        entryKey
      }, entry.type === SelectorTypeDefinitions.Filter && isMultiAttributionBehavior(entry.event.behavior) ? {
        readOnlyEvent: true
      } : {}), isUserBasedEntry(entry) ? {
        readOnlyEvent: this.props.reportName !== ReportName.Insights,
        event: _objectSpread(_objectSpread({}, entry.event), entry.dataGroupId ? {
          label: "All ".concat(print(this.getSelectedGroupLabel()), "(s)")
        } : {})
      } : {}) as Entry;
    });
  }
  private getEmptyEntry() {
    const defaultEmptyEntry = this.props.emptyPlaceholder;
    // Set "Unique" as default aggregation for count menu
    if (this.hasNewQBUI() && defaultEmptyEntry && isQuerySuperEntry(defaultEmptyEntry)) {
      defaultEmptyEntry.aggregationOperator = PRIMITIVE_MATH_TYPES.UNIQUE;
    }
    return defaultEmptyEntry;
  }
  private updateStagedRowsFromEntries() {
    // If the number of entries is less than the mininum number of the required entries,
    // fill in the missing blocks with an empty placeholder entry
    if (this.props.minEntries && this.props.entries.length < this.props.minEntries) {
      // If the missing blocks are already filled, skip
      if (this.state.stagedRows.length >= this.props.minEntries) {
        return;
      }
      // If the missing blocks are not filled, fill in with an empty placeholder entry
      const defaultEmptyEntry = this.getEmptyEntry();
      if (defaultEmptyEntry) {
        const stagedRows = this.mapEntriesToStagedRows(this.props.entries);
        for (let i = 0; i < this.props.minEntries - this.props.entries.length; i++) {
          stagedRows.push(_objectSpread(_objectSpread({}, defaultEmptyEntry), {}, {
            entryKey: this.nextEntryKey++
          }));
        }
        this.setState({
          stagedRows
        });
      }
      return;
    }
    const stagedRows = this.mapEntriesToStagedRows(this.props.entries);
    this.setState({
      placeholders: {},
      stagedRows
    }, () => this.autoactivatePlaceholders());
  }
  public getDropPosition(ev: DragEvent | React.DragEvent, toIndex: number) {
    const dropTarget = ev.currentTarget as HTMLElement;

    // simulate a drop to check the special "on" state
    const result = addEntryFromExternalSection(this, this.props.dragAndDropManager.draggingEl, this.props.dragAndDropManager.draggingEntry, toIndex, this.nextEntryKey + 1);
    const {
      modifyExisting,
      entry
    } = result !== null && result !== void 0 ? result : {};
    if (modifyExisting) {
      return DROP_POSITION.ON;
    }
    if (this.props.dragAndDropManager.draggingEl !== this && entry && this.isEntryPlaceholder(entry, toIndex) || !this.getEntries().length) {
      return DROP_POSITION.HEADER;
    }
    const rect = dropTarget.getBoundingClientRect();
    return ev.clientY - (rect.top + rect.height / 2) < 0 ? DROP_POSITION.ABOVE : DROP_POSITION.BELOW;
  }

  /**
   * Shifts the step at fromIndex to toIndex,
   * then triggers an event update to requery the data
   */
  private handleEntryStepDrop(fromIndex: RowAddress, toIndex: number): void {
    var _draggingEl$props$tra, _this$props$trackProp;
    let copy = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    // todo: this needs to be refactored. we should NEVER access another component's props/state directly
    const draggingEntry = this.props.dragAndDropManager.draggingEntry;
    const draggingEl = this.props.dragAndDropManager.draggingEl;
    const newEntryKey = this.nextEntryKey++;
    const isCopy = copy || (draggingEl === null || draggingEl === void 0 ? void 0 : draggingEl.props.readonly);
    const result = addEntryFromExternalSection(this, draggingEl, draggingEntry, toIndex, newEntryKey, isCopy);
    const {
      entries,
      entry,
      index,
      formulaRefUpdates
    } = result;
    const trackingProps = {
      Clone: copy,
      'From index': fromIndex.join("."),
      'To index': this.getRowAddress(toIndex).join("."),
      'From section': draggingEl === null || draggingEl === void 0 || (_draggingEl$props$tra = draggingEl.props.trackProperties) === null || _draggingEl$props$tra === void 0 ? void 0 : _draggingEl$props$tra.sectionName,
      'To section': (_this$props$trackProp = this.props.trackProperties) === null || _this$props$trackProp === void 0 ? void 0 : _this$props$trackProp.sectionName
    };
    if (entry && this.isEntryPlaceholder(entry, index)) {
      if (result.modifyExisting) {
        // set placeholders if dragging property entry to inline filter
        const filterEntry = isSegmentEntry(draggingEntry) ? segmentToFilter(draggingEntry) : draggingEntry;
        this.showPlaceholderForEntry(EntryPart.Filters, index, filterEntry);
      } else {
        this.setStagingRow(entry);
      }
    } else {
      this.commitAndTrackUpdate(entries, entry, {
        index,
        trackingProps,
        action: Action.Drag,
        formulaRefUpdates
      });
    }
    if (draggingEntry && this.isEntryPlaceholder(draggingEntry, index)) {
      draggingEl.abortRowStage();
    }

    // if we are dragging from a different section and aren't cloning we need to clean up the original entry
    if (draggingEl !== this && !isCopy) {
      const oldEntryIdx = draggingEl.getEntries().findIndex(target => target.entryKey === draggingEntry.entryKey);
      if (oldEntryIdx !== -1) {
        // suppress tracking on this since we already tracked it above
        draggingEl.updateEntries(Action.Remove, {
          index: oldEntryIdx,
          suppressTracking: true
        });
      }
    }
    draggingEl.onEntryStepDragClear();
    this.autoactivatePlaceholders(true);
  }

  /**
   * Generates a new entry with a default operator applied
   */
  private getNewEntry(type: SelectorType): Entry {
    let {
      aggregationOperator,
      event,
      dateRange
    }: {
      event?: EntryItem;
      aggregationOperator?: MATH_TYPE;
      dateRange?: DateRangeAttributes;
    } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    const placeHolderEntry = this.getPlaceholderEventEntry(type);
    const isDateRangeType = this.isDateRangeType(type);

    // TODO(pablo): move all of these QuerySuperEntry handlers
    // to different classes to avoid generics in this class
    const emptyEntry = getNewEmptyEntry({
      type,
      section: this
    });
    if (emptyEntry) {
      return emptyEntry;
    }
    return _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, placeHolderEntry), this.props.dataGroupId ? {
      dataGroupId: this.props.dataGroupId
    } : null), isDateRangeType ? {
      dateRange: dateRange !== null && dateRange !== void 0 ? dateRange : this.getDefaultDateValue()
    } : {}), {}, {
      aggregationOperator: aggregationOperator !== null && aggregationOperator !== void 0 ? aggregationOperator : this.getEntryDefaultOperator(type),
      aggregationOperatorPerUser: null,
      event: event || placeHolderEntry.event
    }, isUserBasedEntry(placeHolderEntry) ? {
      readOnlyEvent: true
    } : {}) as QuerySuperEntry;
  }
  private getEntryDefaultOperator(type: SelectorType) {
    // TODO: Return null for filter/segment selector types
    if (type === ResourceType.Cohort) return PRIMITIVE_MATH_TYPES.UNIQUE;
    if (type === ResourceType.User) return PRIMITIVE_MATH_TYPES.TOTAL;
    // Default count type for count menu is "Unique"
    return this.hasNewQBUI() ? PRIMITIVE_MATH_TYPES.UNIQUE : PRIMITIVE_MATH_TYPES.TOTAL;
  }

  /**
   * Returns an empty entry for a given source
   */
  private getPlaceholderEventEntry(type: SelectorType): QueryEventEntry | QueryPropertyEntry {
    const forExclusionStep = this.props.entryType === EntryType.ExclusionEvent;
    const event = null;
    if (!isPropertySelectorType(type)) {
      const label = this.props.i18n.print(this.props.addEntryLabel) || this.props.i18n.print(SelectorTypesLabels[type]);
      return _objectSpread(_objectSpread({}, forExclusionStep ? EMPTY_EXCLUSION_EVENT_ENTRY : EMPTY_EVENT_ENTRY), {}, {
        event: _objectSpread(_objectSpread({}, EMPTY_EVENT_ENTRY.event), {}, {
          label
        }),
        type
      }) as QueryEventEntry;
    }
    return _objectSpread(_objectSpread({}, forExclusionStep ? EMPTY_EXCLUSION_EVENT_ENTRY : EMPTY_EVENT_ENTRY), {}, {
      type,
      event
    });
  }
  private getSelectedAggregationData(_ref16: {
    entry: QueryMetricEntry;
    forNewWorkflow?: boolean;
  }) {
    let {
      entry,
      forNewWorkflow = false
    }: {
      entry: QueryMetricEntry;
      forNewWorkflow?: boolean;
    } = _ref16;
    return getSelectedAggregationData({
      entry,
      dataGroupId: this.props.dataGroupId,
      dataGroups: this.state.dataGroups,
      forNewWorkflow
    });
  }

  /**
   * Filters aggregation functions to match the provided selector types and optionally defined operators
   */
  private filterSelectorTypeAggregationSections(filters: { [key in SelectorType]?: Array<{
    operator: MATH_TYPE;
    forProperty: boolean;
  }> }, aggregationItems: SelectorTypeAggregation[], isBehaviorSelectorType: boolean): Array<Section<SelectorTypeAggregation>> {
    const matchesType = (aggregationItem: SelectorTypeAggregation) => {
      return Object.entries(filters).some(_ref17 => {
        var _AggregationsMappings;
        let [filterType, filterValues] = _ref17;
        const matchesSelectorType = (_AggregationsMappings = AggregationsMappings.get(filterType as SelectorType)) === null || _AggregationsMappings === void 0 ? void 0 : _AggregationsMappings.has(aggregationItem);
        let matchesOperator = filterValues ? filterValues.some(value => value.operator === aggregationItem.operator && (aggregationItem.forProperty || false) === value.forProperty) : true;

        /** @todo [pablo] provide behaviors with a specific list of aggregations */
        matchesOperator = (aggregationItem.operator === PRIMITIVE_MATH_TYPES.MOST_FREQUENT || aggregationItem.operator === PRIMITIVE_MATH_TYPES.FIRST_VALUE) && !isBehaviorSelectorType ? false : matchesOperator;
        return matchesSelectorType && matchesOperator;
      });
    };
    const sectionItems: Array<SectionItem<SelectorTypeAggregation>> = aggregationItems.filter(aggregationItem => {
      // filter out nested menu options that do not match filters
      if (Array.isArray(aggregationItem.subSections)) {
        return aggregationItem.subSections.some(matchesType);
      }
      return matchesType(aggregationItem);
    }).map(aggregationItem => _objectSpread(_objectSpread({}, this.generateAggregationSectionItem(aggregationItem)), {}, {
      subSections: aggregationItem.subSections ? this.filterSelectorTypeAggregationSections(filters, aggregationItem.subSections, isBehaviorSelectorType) : null
    }));
    return sectionItems.length ? [{
      items: sectionItems
    }] : [];
  }

  /**
   * Returns aggregation functions for all supported row types
   */
  private getSelectorAggregationsForEntry(): Array<Section<SelectorTypeAggregation>> {
    const listTypes = this.getAddListTypeItems();

    /** @todo [pablo] remove behavior specific code from main component */
    let isBehaviorSelectorType = false;
    const filters = mapValues(listTypes, (rowTypeOptions, rowType) => {
      let operatorFilters = null;
      if (rowType === ResourceType.Event) {
        var _this$props$eventAggr, _this$props$propertyA;
        const aggregationFilters = (_this$props$eventAggr = this.props.eventAggregatorFunctions) === null || _this$props$eventAggr === void 0 ? void 0 : _this$props$eventAggr.map(operator => ({
          operator,
          forProperty: false
        }));
        const propertyAggregationFilters = (_this$props$propertyA = this.props.propertyAggregatorFunctions) === null || _this$props$propertyA === void 0 ? void 0 : _this$props$propertyA.map(operator => ({
          operator,
          forProperty: true
        }));
        operatorFilters = aggregationFilters || propertyAggregationFilters ? [...aggregationFilters, ...propertyAggregationFilters] : null;
      }

      // currently only distinguishing property between behaviorSelectorType and regular selector types is the includeDateRange field
      // listTypes currently only has either all behaviorSelectorType or no behaviorSelectorTypes

      isBehaviorSelectorType = isBehaviorSelectorType || (rowTypeOptions === null || rowTypeOptions === void 0 ? void 0 : rowTypeOptions.includeDateRange);
      return operatorFilters;
    });
    return this.filterSelectorTypeAggregationSections(filters, NESTED_AGGREGATIONS_MENU_ITEMS, isBehaviorSelectorType);
  }

  /**
   * Checks if a given entry is not commited to the list
   */
  private isStagedEntry(index: number) {
    return index >= this.state.stagedRows.length;
  }

  /**
   * Checks if a given entry is an empty block with no event/property selected
   */
  private isEmptyPlaceholder(entry: Entry, forceIndex?: number) {
    return this.isEntryPlaceholder(entry, forceIndex) && !this.isEntryIntermediatePlaceholder(entry, forceIndex);
  }

  /**
   * Retrieve an entry from the list.
   *
   * Return type is overridable because the type combinations can't be expressed
   * at this generic level.
   */
  // todo: this generic does literally nothing
  private getEntry<T extends Entry>(index: number): T {
    return this.getEntries(true)[index] as T;
  }

  // todo: public because used in entry transforms. pass them in instead
  public getEntries(): Entry[] {
    let appendPlaceholder = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    return [...this.state.stagedRows,
    // placeholder row
    ...(appendPlaceholder && this.state.stagedRow ? [this.state.stagedRow] : [])];
  }

  /**
   * Finds the index of an entry.
   */
  private getEntryIndex(entry: Entry): number {
    return this.getEntries(true).indexOf(entry);
  }

  /**
   * Performs one of Action operations on the staged entries
   */
  // todo: public because drag and drop util uses it. audit!
  public updateEntries(action: Action, _ref18: {
    entry?: Entry;
    index: number;
    trackingProps?: Obj;
    queryReason?: QueryReason;
    suppressTracking?: boolean;
    requery?: boolean;
  }): UpdateStatus {
    var _entry;
    let {
      entry,
      index,
      trackingProps,
      queryReason,
      suppressTracking = false,
      requery = true
    }: {
      entry?: Entry;
      index: number;
      trackingProps?: Obj;
      queryReason?: QueryReason;
      suppressTracking?: boolean;
      requery?: boolean;
    } = _ref18;
    let entries = this.getEntries();
    entry = (_entry = entry) !== null && _entry !== void 0 ? _entry : entries[index];
    if (this.isStagedEntry(index) && action === Action.Edit) {
      // when a staged entry row is ready to be commited, we override
      // the action to commit the staged entry
      if (this.shouldCommitUpdate(action, entry, index)) {
        action = Action.Add;
      } else {
        this.setState({
          stagedRow: _objectSpread(_objectSpread({}, entry), {}, {
            entryKey: this.nextEntryKey++
          })
        });
        return {
          dispatchedUpdate: false
        };
      }
    } else if (entry && this.shouldCommitUpdate(action, entry, index)) {
      entry.persistedEntry = null;
    } else if (action === Action.Edit) {
      // for any action that results in changing from a commited entry to a staged one
      // we persist the commited state in persistedEntry so that all commited entries
      // are preserved when another change occurs
      const sourceEntry = this.getEntry(index);
      const isStagingChanges = this.isStagingEntryChanges(sourceEntry, entry);
      entry.persistedEntry = isStagingChanges ? sourceEntry : null;
    }
    let shouldUpdateFormulaRefs = false;
    let savedBehaviorId: number | undefined;
    switch (action) {
      case Action.Add:
        entries = [...entries, _objectSpread(_objectSpread({}, entry), {}, {
          entryKey: this.nextEntryKey++
        })];
        break;
      case Action.Clone:
        shouldUpdateFormulaRefs = true;
        entry = cloneDeep(entry);
        if (isFormulaEntry(entry)) {
          entry.event = _objectSpread(_objectSpread({}, entry.event), {}, {
            name: "Duplicate of ".concat(entry.event.name)
          });
        } else if (isMetricEntry(entry)) {
          entry = _objectSpread({}, entry);
        } else if (!isPropertyEntry(entry)) {
          entry.event = omit(entry.event, "renamed") as EventItem | SavedCohortItem;
        }
        entries = insertAtIndex(entries, index + 1, _objectSpread(_objectSpread({}, entry), {}, {
          entryKey: this.nextEntryKey++
        }));
        break;
      case Action.Ungroup:
      case Action.Edit:
      case Action.EditFlowSteps:
        entries = replaceByIndex(entries, index, entry);
        break;
      case Action.Remove:
        shouldUpdateFormulaRefs = true;
        entry = entries[index];
        if (entry) entries = removeByIndex(entries, index);
        break;
      case Action.Group:
        entry = cloneDeep(entries[index]);
        entries = replaceByIndex(entries, index, {
          filters: [],
          type: SelectorTypeDefinitions.GroupedEvent,
          event: [entry, this.getPlaceholderEventEntry(ResourceType.Event)]
        });
        break;
      case Action.SaveBehavior:
        if (isMetricEntry(entry)) {
          savedBehaviorId = entry.behavior.id;
        }
        entries = replaceByIndex(entries, index, entry);
        break;
    }

    // update any formula references
    let formulaRefUpdates: Record<string, string> | undefined;
    if (shouldUpdateFormulaRefs) {
      formulaRefUpdates = calculateFormulaReferenceChanges(this.getEntries(), entries);
      entries = updateFormulaReferences(entries, formulaRefUpdates);
    }
    return this.commitAndTrackUpdate(entries, entry, {
      queryReason,
      action,
      index,
      trackingProps,
      suppressTracking,
      requery,
      formulaRefUpdates,
      savedBehaviorId
    });
  }
  private shouldCommitUpdate(action: Action, entry: Entry, index: number): boolean {
    if (action === Action.Group) return false;
    if ([Action.Remove, Action.Clone].includes(action)) return true;
    return entry && !this.isEntryPlaceholder(entry, index);
  }
  private commitAndTrackUpdate(entries: Entry[], entry: Entry, _ref19: Partial<{
    index: number;
    queryReason: QueryReason;
    action: Action;
    trackingProps: Obj;
    suppressTracking?: boolean;
    requery: boolean;
    formulaRefUpdates?: Record<string, string>;
    savedBehaviorId?: number;
  }>): UpdateStatus {
    let {
      index,
      queryReason = QueryReason.QbOtherUpdate,
      action,
      trackingProps,
      suppressTracking = false,
      requery = true,
      formulaRefUpdates,
      savedBehaviorId
    }: Partial<{
      index: number;
      queryReason: QueryReason;
      action: Action;
      trackingProps: Obj;
      suppressTracking?: boolean;
      requery: boolean;
      formulaRefUpdates?: Record<string, string>;
      savedBehaviorId?: number;
    }> = _ref19;
    const updateStatus: UpdateStatus = {
      dispatchedUpdate: false
    };

    // If the number of entries is less than the minimum number of the required entries,
    // fill in the missing blocks with an empty placeholder entry
    let stagedRows = entries;
    if (this.props.minEntries && entries.length < this.props.minEntries) {
      const defaultEmptyEntry = this.props.emptyPlaceholder;
      stagedRows = [...this.state.stagedRows];
      if (defaultEmptyEntry) {
        stagedRows[index] = defaultEmptyEntry;
      } else {
        stagedRows[index] = entry;
      }
    }
    this.setState({
      hasOpenDropdown: false,
      stagedRows
    });

    // sometimes updates can fire in rapid succession between renders, and since updateEntries is dependent
    // on the latest stagedRows, certain updates can disappear. this is a React hack to ensure that the latest
    // value is in state when the next update needs to use it
    // todo(micah): refactor state management in this component to avoid the need for this
    this.state.stagedRows = stagedRows;

    // TODO(pablo): avoid managing placeholder entries at the report level
    // as a first iteration, the ideal behavior is reports should not query
    // for empty rows but this behavior has added complexity for formulas
    // in Insights, exclusion steps and attribution in Funnels, as a side effect
    // any staged changes from rows other than the commited change or the staged
    // row will be lost on the next update to the entries attribute
    const shouldCommit = this.shouldCommitUpdate(action, entry, index);
    if (shouldCommit) {
      var _this$props$onUpdate, _this$props;
      captureWorkflowTiming(this, {
        key: "query-update",
        name: "QB Update to Query Start",
        isComplete: queryStartedWorkflowComplete
      });
      const committedEntries: QuerySuperEntry[] = entries.map(et => {
        var _et$persistedEntry;
        return _objectSpread(_objectSpread({}, omit((_et$persistedEntry = et.persistedEntry) !== null && _et$persistedEntry !== void 0 ? _et$persistedEntry : et, ["persistedEntry", "entryKey"])), {}, {
          type: et.type
        }) as QuerySuperEntry;
      });
      this.setState({
        openEntry: {}
      });
      (_this$props$onUpdate = (_this$props = this.props).onUpdate) === null || _this$props$onUpdate === void 0 || _this$props$onUpdate.call(_this$props, {
        entries: committedEntries,
        updatedEntry: entry,
        updatedEntryIndex: index,
        requery,
        formulaRefUpdates,
        savedBehaviorId,
        queryReason
      });
      updateStatus.dispatchedUpdate = true;

      // abort current staged row when committing an entry
      if (action === Action.Add) {
        this.abortRowStage(true);
      }
      this.autoactivatePlaceholders();
    }

    // behavioral filters are currently being transformed from PropertyFilter[] into QueryBehaviorEntry[] which
    // uses an `event` entry selector type, these interactions are tracked as `Add event`, `Remove event`, etc..
    // until we can define these purely with a `behavior` entry selector type, we transform the type for tracking purposes
    // entry.type = SelectorTypeDefinitions.Behavior;
    /** @todo [pablo] define behaviors and behavioral filters with pure `behavior` selector types */
    if (entry && (entry as QueryBehaviorEntry).dateRange && isEventBasedEntry(entry)) {
      const behaviorEntry = _objectSpread(_objectSpread({}, entry), {}, {
        type: SelectorTypeDefinitions.Behavior
      }) as QueryBehaviorEntry;
      this.trackAction(index, behaviorEntry, action, _objectSpread(_objectSpread({}, trackingProps), getBehaviorEntryTrackingProps(behaviorEntry)));
    } else if (!suppressTracking && action !== Action.Edit || entry && isPropertyEntry(entry) || !suppressTracking && entry && isFormulaEntry(entry)) {
      // edit actions are tracked with more detailed elsewhere
      this.trackAction(index, entry, action, trackingProps);
    }

    // Some updates to the deeply nested `stagedRows` object don't trigger a re-render automatically.
    // Manually re-render the component to ensure the latest stagedRows are displayed.
    this.forceUpdate();
    return updateStatus;
  }

  /**
   * Removes an entry.
   */
  private removeEntry(index: number, queryReason?: QueryReason) {
    if (this.isStagedEntry(index)) {
      this.abortRowStage();
    } else {
      this.updateEntries(Action.Remove, {
        index,
        queryReason
      });
    }
  }
  private dispatchTrack(eventName: string, props: Obj): void {
    const properties = _objectSpread(_objectSpread(_objectSpread({}, props), this.props.trackProperties), {}, {
      ["usage context"]: TrackingUsageContext.QueryBuilder
    });
    emitTrackEvent(this.rootEl.current, {
      eventName,
      props: properties,
      cancelable: true
    });
  }

  // TODO(pablo): find a better way to abstract this
  private getQueryBlockConfig(entry: Entry, index?: number): Obj {
    const type = entry.type as SelectorType;
    switch (type) {
      case SelectorTypeDefinitions.PropertyExpansion:
        return {
          type: MenuType.Breakdown,
          readonly: this.props.readonly,
          showSpecialDatePropertyUnit: true,
          excludeAutotrackEvents: this.props.excludeAutotrackEvents,
          excludeDimensionProperties: this.props.excludeDimensionProperties,
          includeCustomProperties: this.props.includeCustomProperties,
          showBorrowedProperties: this.props.showBorrowedProperties
        };
      case ResourceType.Event:
      case SelectorTypeDefinitions.FlowsEvent:
        return {
          type: MenuType.Events,
          allDataGroups: this.props.dataGroupsEnabled,
          readonly: this.props.readonly,
          excludeAutotrackEvents: this.props.excludeAutotrackEvents,
          excludeDimensionProperties: this.props.excludeDimensionProperties,
          includeCustomProperties: this.props.includeCustomProperties,
          showBorrowedProperties: this.props.showBorrowedProperties,
          isBlockedPeople: this.props.isBlockedPeople,
          projectHasPeople: this.props.projectHasPeople,
          projectHasCohorts: this.props.projectHasCohorts && this.props.allowCohortFilter,
          showAllEvents: this.props.showAllEvents,
          showAnyEvent: this.props.showAnyEvent,
          showSessionEvent: this.props.showSessions,
          showTopEvents: this.props.restrictTopEventsOnly ? this.props.showTopEvents && index === 0 && this.state.stagedRows.length < 2 : this.props.showTopEvents,
          supportB2bCohorts: this.props.supportB2bCohorts
        };
      case ResourceType.Cohort:
        return {
          type: MenuType.Properties,
          includeCustomProperties: this.props.includeCustomProperties,
          showBorrowedProperties: this.props.showBorrowedProperties,
          projectHasCohorts: true,
          hideEvents: true,
          projectHasPeople: false,
          supportB2bCohorts: this.props.supportB2bCohorts,
          inlineCohortsOverride: this.props.inlineCohortsOverride
        };
      case ResourceType.User:
        return {
          type: MenuType.Properties,
          excludeDimensionProperties: this.props.excludeDimensionProperties,
          includeCustomProperties: this.props.includeCustomProperties,
          showBorrowedProperties: this.props.showBorrowedProperties,
          projectHasCohorts: false,
          hideEvents: true,
          projectHasPeople: true
        };
      case SelectorTypeDefinitions.Filter:
      case SelectorTypeDefinitions.Segment:
        {
          var _this$props$propertyC;
          const segment = entry as Segment;
          let config: Obj = {
            cohortMultiselect: true,
            allDataGroups: this.props.dataGroupsEnabled,
            dataGroupIds: convertDataGroupIdToSet(this.props.dataGroupId, this.props.dataGroupIds),
            enableAllDataGroupsForScdProps: this.props.enableAllDataGroupsForScdProps,
            eventNames: this.props.eventNames,
            excludeDimensionProperties: this.props.excludeDimensionProperties,
            includeCustomProperties: this.props.includeCustomProperties,
            showBorrowedProperties: this.props.showBorrowedProperties,
            projectHasCohorts: this.props.projectHasCohorts,
            hideEvents: this.props.hideEvents,
            projectHasPeople: this.props.projectHasPeople,
            supportB2bCohorts: this.props.supportB2bCohorts,
            hideEditControls: this.props.hideEditControls,
            propertyCombineMode: (_this$props$propertyC = this.props.propertyCombineMode) !== null && _this$props$propertyC !== void 0 ? _this$props$propertyC : undefined
          };
          if (type === SelectorTypeDefinitions.Filter) {
            config = _objectSpread(_objectSpread({}, config), {}, {
              type: MenuType.Filter,
              otherFilterOptions: this.props.otherFilterOptions.filter(option => {
                return !isEventSegment(segment) || !isAggregateUserFreqByGroupSegmentation(segment.event) || option.value !== SPECIAL_EVENTS.SELECTED_STEP;
              })
            });
          } else {
            config = _objectSpread(_objectSpread({}, config), {}, {
              type: MenuType.Properties,
              showAllTab: true,
              otherGroupByOptions: this.props.otherGroupByOptions.filter(option => {
                if (isEventSegment(segment) && isAggregateUserFreqByGroupSegmentation(segment.event) && option.value === SPECIAL_EVENTS.SELECTED_STEP) {
                  return false;
                }
                if (option.value !== BEHAVIOR_MULTI_ATTRIBUTION.value) {
                  return true;
                }
                const hasMultiAttributionProp = this.props.entries.some(isMultiAttributionSegmentEntry);
                const currentEntry = this.props.entries[index];
                return currentEntry && isMultiAttributionSegmentEntry(currentEntry) || !hasMultiAttributionProp;
              })
            });
          }
          return config;
        }
      case SelectorTypeDefinitions.Behavior:
        return {
          type: MenuType.Events,
          includeCustomProperties: this.props.includeCustomProperties,
          showBorrowedProperties: this.props.showBorrowedProperties,
          hideEvents: false,
          projectHasPeople: true,
          showAllEvents: this.props.showAllEvents,
          showAnyEvent: this.props.showAnyEvent,
          showSessionEvent: this.props.showSessions,
          showTopEvents: this.props.showTopEvents
        };
      case SelectorTypeDefinitions.EventLaunch:
        return {
          type: MenuType.Events,
          excludeAutotrackEvents: this.props.excludeAutotrackEvents,
          includeCustomProperties: this.props.includeCustomProperties,
          showBorrowedProperties: this.props.showBorrowedProperties,
          isBlockedPeople: this.props.isBlockedPeople,
          projectHasPeople: this.props.projectHasPeople,
          showAllEvents: this.props.showAllEvents,
          showAnyEvent: this.props.showAnyEvent,
          showSessionEvent: this.props.showSessions,
          showTopEvents: this.props.showTopEvents
        };
      case ResourceType.Formula:
        return {
          type: MenuType.Formulas
        };
      case ResourceType.Metric:
        return {
          type: MenuType.SavedMetrics
        };
    }
    return {};
  }

  // todo: public because its called in drag and drop stuff.
  public abortRowStage() {
    var _this$props$onAbortSt, _this$props2;
    let committing = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    this.setState({
      openEntry: {},
      hasOpenDropdown: false,
      stagedRow: null
    });
    (_this$props$onAbortSt = (_this$props2 = this.props).onAbortStage) === null || _this$props$onAbortSt === void 0 || _this$props$onAbortSt.call(_this$props2, committing);
  }
  private setActiveEntry(activeState: `open` | `close`, _ref20: {
    index: number;
    part: EntryPart;
  }) {
    let {
      index,
      part
    }: {
      index: number;
      part: EntryPart;
    } = _ref20;
    this.setState(state => {
      const updates: Partial<QueryEntrySectionState> = {};
      if (activeState === "open") {
        updates.openEntry = {
          index,
          part
        };
      } else {
        var _state$placeholders$i;
        // remove the placeholder if we're closing it
        if ((_state$placeholders$i = state.placeholders[index]) !== null && _state$placeholders$i !== void 0 && _state$placeholders$i.includes(part)) {
          const placeholders = _objectSpread({}, state.placeholders);
          delete placeholders[index];
          updates.placeholders = placeholders;
        }

        // if this is the currently open entry, close it
        if (state.openEntry.index === index && state.openEntry.part === part) {
          updates.openEntry = {};
        }
      }
      return Object.keys(updates).length ? updates as QueryEntrySectionState : null;
    });
  }

  /**
   * Generates the type of items that can be added to the list
   * eg. Event or Cohort
   */
  private getAddListTypeItems(): TypeList {
    const sections: TypeList = {};
    const types = this.props.rowTypes;

    // populate selected items
    for (const type of types) {
      let selectorType: SelectorType;
      let disabledReason: string;
      if (isObject(type)) {
        selectorType = type.value;
        disabledReason = type.disabledReason;
      } else {
        selectorType = type;
      }
      if (isObject(type)) {
        sections[selectorType] = _objectSpread(_objectSpread(_objectSpread({}, LIST_TYPES[selectorType]), type), {}, {
          value: selectorType
        }, disabledReason && {
          isDisabled: true,
          tooltip: {
            placement: Placement.Right,
            text: disabledReason
          }
        });
      } else {
        var _LIST_TYPES$selectorT;
        sections[selectorType] = (_LIST_TYPES$selectorT = LIST_TYPES[selectorType]) !== null && _LIST_TYPES$selectorT !== void 0 ? _LIST_TYPES$selectorT : {
          value: selectorType,
          label: selectorType
        };
      }
    }
    const transforms = {
      [ResourceType.Event]: item => {
        return _objectSpread(_objectSpread({}, item), this.props.addEntryLabel ? {
          label: this.props.addEntryLabel
        } : {});
      }
    };
    return mapValues(sections, (value, key) => transforms[key] ? transforms[key](value) : value);
  }
  private getTrackingEvent(type: ResourceTrackingTypes, action: Action): string {
    const forExclusionStep = this.props.entryType === EntryType.ExclusionEvent;
    if (forExclusionStep) {
      type = EntryType.ExclusionEvent;
    }
    return ResourceTrackingEvent[type][action];
  }
  private isStagingEntryChanges(entry: Entry, updatedEntry: Entry) {
    return !this.isEntryPlaceholder(entry) && this.isEntryPlaceholder(updatedEntry);
  }

  /**
   * Formats a given label to include the selected data group (Users, Accounts, etc...)
   */
  private formatDataGroupLabel(label: SelectorTypeAggregationLabel) {
    return formatDataGroupLabel({
      label,
      dataGroupId: this.props.dataGroupId,
      dataGroups: this.state.dataGroups
    });
  }
  private getSelectedGroupLabel() {
    let isPlural = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    const dataGroupId = this.props.dataGroupId;
    return getSelectedGroupLabel({
      dataGroupId,
      dataGroups: this.state.dataGroups,
      isPlural
    });
  }
  private async fetchDataGroups() {
    const projectId = this.props.projectId;
    const workspaceId = this.props.workspaceId;
    const paramsUnchanged = () => projectId === this.props.projectId && workspaceId === this.props.workspaceId;
    if (this.props.dataGroupsEnabled) {
      const dataGroups = await dataGroupsClient.query({
        projectId,
        workspaceId
      });
      if (paramsUnchanged()) {
        this.setState({
          dataGroups: [DEFAULT_DATA_GROUP, ...dataGroups]
        });
        this.updateStagedRowsFromEntries();
      }
    }
  }
  private scrollNthRowIntoView(index: number) {
    if (index > 0) {
      var _this$rootEl$current;
      const lastEntryRowEl = (_this$rootEl$current = this.rootEl.current) === null || _this$rootEl$current === void 0 ? void 0 : _this$rootEl$current.querySelector(".mp-query-entry-section-body :nth-child(".concat(index, ")"));
      if (lastEntryRowEl) {
        window.requestAnimationFrame(() => {
          var _lastEntryRowEl$scrol;
          return (_lastEntryRowEl$scrol = lastEntryRowEl.scrollIntoView) === null || _lastEntryRowEl$scrol === void 0 ? void 0 : _lastEntryRowEl$scrol.call(lastEntryRowEl, true);
        });
      }
    }
  }

  /**
   * Stages or adds a new entry row for a given SelectorType or SelectorTypeAggregation
   */
  private addEntryRow(type: SelectorType) {
    var _index, _typeDefinition$place;
    let {
      index,
      typeSelect = false
    }: {
      index?: number;
      typeSelect?: boolean;
    } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    (_index = index) !== null && _index !== void 0 ? _index : index = this.getEntries(true).length;
    this.scrollNthRowIntoView(index);

    // checks for static defined row types and append that to the list
    const typeDefinition = this.getAddListTypeItems()[type];
    const baseEntry = (_typeDefinition$place = typeDefinition.placeholderEntry) !== null && _typeDefinition$place !== void 0 ? _typeDefinition$place : {};
    const entry = Object.assign(this.getNewEntry(type, {
      event: baseEntry === null || baseEntry === void 0 ? void 0 : baseEntry.event
    }), baseEntry);
    this.dispatchTrack(QueryEntrySectionTrackingEvent.AddEntryRow, {
      type
    });
    // If selecting a row type or adding multi metrics entry when there is only an empty placeholder (intial empty state),
    // replace the existing entry row with a newly selected row type and open a placeholder block instead of adding a new entry
    // this section will just update the UI for the placeholder and open a dropdown
    // the check for ResourceType.User is because when we select "Profile" metric, it should be commited right away
    if ((typeSelect || type === SelectorTypeDefinitions.Metrics) && type !== ResourceType.User && this.state.stagedRows.length === 1 && this.isEmptyPlaceholder(this.getEntry(0))) {
      this.setState({
        openEntry: {
          index: 0,
          part: this.getEntryDefaultActiveEntryPart(entry, 0)
        },
        stagedRows: [_objectSpread(_objectSpread({}, entry), {}, {
          entryKey: this.nextEntryKey++
        })]
      });
      return;
    }
    if (this.isEntryPlaceholder(entry, index)) {
      this.setStagingRow(_objectSpread(_objectSpread(_objectSpread({}, this.getNewEntry(type)), baseEntry), {}, {
        entryKey: this.nextEntryKey++
      }));
    } else {
      if (this.state.stagedRows.length === 1) {
        if (this.isEmptyPlaceholder(this.getEntry(0))) {
          this.updateEntries(Action.Edit, {
            entry,
            index: 0
          });
        } else {
          this.updateEntries(Action.Add, {
            entry,
            index: 0
          });
        }
      } else {
        this.updateEntries(Action.Add, {
          entry,
          index
        });
      }
    }
  }
  private isEntryPlaceholder(entry: Entry, forceIndex?: number) {
    var _rowConfig$isPlacehol, _rowConfig$isPlacehol2;
    const rowProps = this.getEntryProps(entry, forceIndex);
    const rowConfig = getEntryRowHelpers(entry);
    return (_rowConfig$isPlacehol = rowConfig === null || rowConfig === void 0 || (_rowConfig$isPlacehol2 = rowConfig.isPlaceholder) === null || _rowConfig$isPlacehol2 === void 0 ? void 0 : _rowConfig$isPlacehol2.call(rowConfig, rowProps)) !== null && _rowConfig$isPlacehol !== void 0 ? _rowConfig$isPlacehol : false;
  }
  private isEntryIntermediatePlaceholder(entry: Entry, forceIndex?: number) {
    var _rowConfig$isIntermed, _rowConfig$isIntermed2;
    const rowProps = this.getEntryProps(entry, forceIndex);
    const rowConfig = getEntryRowHelpers(entry);
    return (_rowConfig$isIntermed = rowConfig === null || rowConfig === void 0 || (_rowConfig$isIntermed2 = rowConfig.isIntermediatePlaceholder) === null || _rowConfig$isIntermed2 === void 0 ? void 0 : _rowConfig$isIntermed2.call(rowConfig, rowProps)) !== null && _rowConfig$isIntermed !== void 0 ? _rowConfig$isIntermed : false;
  }
  private getEntryDefaultActiveEntryPart(entry: Entry, forceIndex?: number) {
    const rowProps = this.getEntryProps(entry, forceIndex);
    const rowConfig = getEntryRowHelpers(entry);
    if (rowConfig && rowConfig.getDefaultActiveEntryPart) {
      return rowConfig.getDefaultActiveEntryPart(rowProps);
    }
    return this.isEntryPlaceholder(entry, forceIndex) ? EntryPart.Value : null;
  }
  private handleEntryRowSelect(entry: Entry, prevEntry: Entry, item: MultiMetricsSectionItem, rowIndex: number) {
    var _rowConfig$handleRowS;
    const rowProps = this.getEntryProps(entry, rowIndex);
    const rowConfig = getEntryRowHelpers(entry);
    return rowConfig === null || rowConfig === void 0 || (_rowConfig$handleRowS = rowConfig.handleRowSelect) === null || _rowConfig$handleRowS === void 0 ? void 0 : _rowConfig$handleRowS.call(rowConfig, rowProps, item, prevEntry);
  }
  private handleEntryRowCreate(entry: Entry, menuType: MultiMetricsMenuType, index: number) {
    var _rowConfig$handleRowC;
    const rowProps = this.getEntryProps(entry, index);
    const rowConfig = getEntryRowHelpers(entry);
    return rowConfig === null || rowConfig === void 0 || (_rowConfig$handleRowC = rowConfig.handleRowCreate) === null || _rowConfig$handleRowC === void 0 ? void 0 : _rowConfig$handleRowC.call(rowConfig, rowProps, menuType);
  }

  // todo: public because used in drag and drop util. audit this.
  /**
   * Gets the props passed to each entry row.
   *
   * @param forceIndex - If provided, the index to use for the entry row props. Otherwise, the index will be inferred from the entry.
   */
  public getEntryProps(entry: Entry, forceIndex?: number): EntryRowProps {
    var _this$state$openEntry, _this$props$showDefau, _this$props$currentPr;
    const index = forceIndex !== null && forceIndex !== void 0 ? forceIndex : this.getEntryIndex(entry);

    // todo: a lot of these do NOT need to be implemented in entry section and can be moved to the row types!
    return {
      activePart: ((_this$state$openEntry = this.state.openEntry) === null || _this$state$openEntry === void 0 ? void 0 : _this$state$openEntry.index) === index ? this.state.openEntry.part : null,
      canDelete: this.canDeleteEntry(),
      currentChartType: this.props.chartType,
      dataGroups: this.state.dataGroups,
      disableCollapse: this.props.disableCollapse || false,
      dispatchTrack: this.dispatchTrack.bind(this),
      entry,
      generateAggregationSectionItem: this.generateAggregationSectionItem.bind(this),
      getDefaultDateValue: this.getDefaultDateValue.bind(this),
      getDropAreas: () => this.getEntryDropAreas(index),
      getEntries: this.getEntries.bind(this),
      getEntryDefaultOperator: () => this.getEntryDefaultOperator(entry.type),
      getEntryTrackingProps: entryOverride => this.getEntryTrackingProps(entryOverride !== null && entryOverride !== void 0 ? entryOverride : entry, index),
      getFilteringCapabilities: entryOverride => this.getRowFilteringCapabilities(entryOverride !== null && entryOverride !== void 0 ? entryOverride : entry),
      getCommonOverflowControls: () => this.getCommonOverflowControls(entry),
      getSelectedAggregationData: this.getSelectedAggregationData.bind(this),
      getSelectedGroupLabel: this.getSelectedGroupLabel.bind(this),
      getSelectorAggregationsForEntry: this.getSelectorAggregationsForEntry.bind(this),
      hasBehavioralFrequencyFilter: this.hasBehavioralFrequencyFilter(entry as QueryMetricEntry),
      hasNewUI: this.hasNewQBUI(),
      hasStagedRow: !!this.state.stagedRow,
      index,
      isDateRangeType: this.isDateRangeType(entry.type),
      isDragging: index === this.state.draggingGripperIndex && this.state.draggingStarted,
      isDraggingEnabled: () => this.canDragEntry(index),
      isDraggingGripper: index === this.state.draggingGripperIndex,
      isEntrySelectorDropdownOpen: this.state.hasOpenEntrySelectorDropdown && this.state.openEntry.index === index,
      showDefaultMetricUnitSelector: (_this$props$showDefau = this.props.showDefaultMetricSelector) !== null && _this$props$showDefau !== void 0 ? _this$props$showDefau : true,
      isDroppingAbove: this.isConnected && !this.props.dragAndDropManager.isDraggingOverChild(this.rootEl.current) && this.state.draggingOverStepIndex === index && this.state.dropPosition === DROP_POSITION.ABOVE,
      isDroppingBelow: this.isConnected && !this.props.dragAndDropManager.isDraggingOverChild(this.rootEl.current) && this.state.draggingOverStepIndex === index && this.state.dropPosition === DROP_POSITION.BELOW,
      isDroppingOn: this.isConnected && !this.props.dragAndDropManager.isDraggingOverChild(this.rootEl.current) && this.state.draggingOverStepIndex === index && this.state.dropPosition === DROP_POSITION.ON,
      isEmptyPlaceholder: () => this.isEmptyPlaceholder(entry),
      isExclusionStepsSection: this.props.entryType === EntryType.ExclusionEvent,
      isFirstEntryStaged: this.isStagedEntry(0),
      isNestedSection: this.props.groupEventBehavior === GroupEventBehaviors.Append || this.props.rowIndex !== null,
      isProfileHistoryEnabled: ((_this$props$currentPr = this.props.currentProject.get().value) === null || _this$props$currentPr === void 0 ? void 0 : _this$props$currentPr.hasFeatureFlag(ProjectFeatureFlag.HistoricalProfileProps)) && !this.props.disableProfileHistory,
      isStaged: this.isStagedEntry(index),
      isStagingEntryChanges: this.isStagingEntryChanges.bind(this),
      metricsCtx: this.isConnected ? this.props.metrics : null,
      behaviorsCtx: this.isConnected ? this.props.behaviors : null,
      nestedSectionParams: this.getNestedSectionParams(),
      numStagedRows: this.state.stagedRows.length,
      onControlSelect: value => this.onEntryAction(value, index),
      onDropdownStateChanged: open => this.setState({
        hasOpenDropdown: open
      }),
      onEntrySelectorDropdownStateChanged: open => this.setState({
        hasOpenEntrySelectorDropdown: open
      }),
      onMetricUnitSelectorDropdownStateChanged: open => this.setState({
        hasOpenMetricUnitSelectorDropdown: open
      }),
      onEntryStepDragClear: this.onEntryStepDragClear.bind(this),
      onEntryStepDragEnter: ev => this.onEntryStepDragEnter(ev, index),
      onEntryStepDragLeave: this.onEntryStepDragLeave.bind(this),
      onEntryStepDragOver: ev => this.onEntryStepDragOver(ev, index),
      onEntryStepDragStart: ev => this.onEntryStepDragStart(ev, index),
      onEntryStepDrop: ev => this.onEntryStepDrop(ev, index),
      onGripperMouseDown: ev => {
        const isWithinUndraggableEls = eventPathContainsSelectors(ev, ["mp-drop-menu", "mp-input"]).containsSelectors;
        if (this.canDragEntry(index)) {
          this.setState({
            draggingGripperIndex: isWithinUndraggableEls ? null : index
          });
        }
      },
      onGroupLabelClick: () => {
        var _this$props$onEntryGr, _this$props3;
        return (_this$props$onEntryGr = (_this$props3 = this.props).onEntryGroupLabelClicked) === null || _this$props$onEntryGr === void 0 ? void 0 : _this$props$onEntryGr.call(_this$props3);
      },
      onSelectMenuChange: (ev, part) => {
        const {
          isOpen
        } = ev.detail;
        const status = isOpen ? "open" : "close";
        this.setActiveEntry(status, {
          index,
          part
        });
      },
      onEntryRowSelect: (entryRow, prevEntry, item, rowIndex) => {
        this.handleEntryRowSelect(entryRow, prevEntry, item, rowIndex);
      },
      onEntryRowCreate: (entryRow, itemType) => {
        this.handleEntryRowCreate(entryRow, itemType, index);
      },
      placeholder: this.state.placeholders[index],
      propagateEvent: ev => _propagateEvent(this.rootEl.current, ev),
      queryBlockConfig: this.getQueryBlockConfig(entry, index),
      renderPrefix: this.props.renderPrefix,
      renderSuffix: this.props.renderSuffix,
      rowLabel: this.props.rowLabel || this.getEntryLabel(index),
      rowLabelTheme: this.props.rowLabelTheme,
      sectionProps: this.props,
      setActivePart: (part, state) => this.setActiveEntry(state, {
        index,
        part
      }),
      showPlaceholder: (part, newEntry) => this.showPlaceholderForEntry(part, index, newEntry),
      trackAction: (action, newEntry, trackingProps) => this.trackAction(index, newEntry, action, trackingProps),
      trackEntrySelectorEvent: ev => this.trackEntrySelectorEvent(ev, index),
      addEntry: (type, entryIndex) => this.addEntryRow(type, {
        index: entryIndex
      }),
      updateEntry: (action, newEntry, options) => this.updateEntries(action, _objectSpread({
        entry: newEntry,
        index
      }, options)),
      removeEntry: queryReason => this.removeEntry(index, queryReason),
      wouldBePlaceholder: en => this.isEntryPlaceholder(en, index),
      abortStage: () => this.abortRowStage()
    };
  }
  private trackAction(index: number, entry: Entry, action: Action, trackingProps?: Obj) {
    /** @todo [pablo] define behaviors and behavioral filters with pure `behavior` selector types */
    if (isEventBasedEntry(entry) && (entry as QueryBehaviorEntry).dateRange) {
      entry = _objectSpread(_objectSpread({}, entry), {}, {
        type: SelectorTypeDefinitions.Behavior
      }) as QueryBehaviorEntry;
      trackingProps = _objectSpread(_objectSpread({}, trackingProps), getBehaviorEntryTrackingProps(entry));
    }
    const consolidatedProps = _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({
      queryOrigin: this.props.queryOrigin
    }, this.getEntryTrackingProps(entry, index)), trackingProps || {}), entry && isMetricEntry(entry) ? {
      'metric type': entry.behavior.type
    } : {}), entry && entry.type === ResourceType.Event ? {
      'is custom event': entry.event.custom
    } : {}), entry.type === ResourceType.Cohort ? {
      'cohort type': isInlineCohortItem(entry.event) ? "inline cohorts" : "cohorts"
    } : {});
    this.dispatchTrack(this.getTrackingEvent(entry.type, action), consolidatedProps);
  }
  private getEntryTrackingProps(entry: Entry, index?: number) {
    return {
      'Entry index': index !== null && index !== void 0 ? index : this.getEntryIndex(entry),
      'Entry type': entry.type
    };
  }

  // todo: move down
  private canAddMultiAttribution(entry: Entry) {
    return this.props.canUseMultiAttribution && !this.isEntryPlaceholder(entry) && !getMultiAttributionFromEntry(entry) && (isEventBasedEntry(entry) || isFormulaEntry(entry)) && isSupportedMultiAttributionTypeForEntry({
      entry,
      metricsResource: this.props.metrics,
      behaviorsResource: this.props.behaviors
    });
  }

  // todo: public because used by entry transforms
  public getRowFilteringCapabilities(entry: Entry): FilteringCapabilities {
    var _event;
    // TODO(CC): get row filter capabilities for metric entry
    if (isMetricEntry(entry)) {
      return {
        canApplyFilter: false,
        canApplyFirstTimeEventFilter: false,
        firstTimeFilterDisabled: false
      };
    }
    const eventValue = entry as QueryEventEntry === null || entry as QueryEventEntry === void 0 || (_event = (entry as QueryEventEntry).event) === null || _event === void 0 ? void 0 : _event.value;
    const canApplyFilter = !this.isEntryPlaceholder(entry) && entry.type !== ResourceType.Cohort && !isFormulaEntry(entry) && !isPropertyEntry(entry) && !this.props.disableAddEventFilters;

    /** @todo [pablo] add support for 1st time filters for behaviors */
    const canApplyFirstTimeEventFilter = canApplyFilter && !hasFirstTimeEventFilter(entry) && !this.props.disableFirstTimeEventFilters && isEventBasedEntry(entry);

    // first time filters are allowed on regular events
    const firstTimeFilterDisabled = canApplyFirstTimeEventFilter && [SPECIAL_EVENTS.SESSION_START, SPECIAL_EVENTS.SESSION_END, SPECIAL_EVENTS.TOP_EVENTS].includes(eventValue);
    return {
      canApplyFilter,
      canApplyFirstTimeEventFilter,
      firstTimeFilterDisabled
    };
  }

  /** @todo [pablo] update cohorts QB to remove this conditional */
  private hasNewQBUI() {
    return queryBuilderUtils.isCoreReport() && this.props.trackName !== "cohorts" || this.props.trackName === "advanced-deletion";
  }

  /**
   * Generates a SectionItem from a given aggregation
   *
   * @param aggregationItem
   */
  private generateAggregationSectionItem(aggregation: SelectorTypeAggregation) {
    return {
      label: this.formatDataGroupLabel(aggregation.label),
      subLabel: aggregation.subLabel ? this.formatDataGroupLabel(aggregation.subLabel) : undefined,
      value: aggregation,
      tooltip: aggregation.description ? {
        text: this.formatDataGroupLabel(aggregation.description),
        size: DisplaySize.Small
      } : null
    };
  }
  render() {
    const {
      print
    } = this.props.i18n;
    const hasNewUI = this.hasNewQBUI();
    const hasNewHeader = hasNewUI && (this.props.headerItems || this.props.sectionTitle);
    const hasStickyHeader = !!this.props.enableStickyHeader;
    const showAddButton = this.canAddRow() && !this.props.disableAddEntry && !hasNewHeader && this.props.groupEventBehavior !== GroupEventBehaviors.Append;
    const displayFooter = this.props.footerItems || showAddButton;
    const showLabels = this.props.rowLabelType !== RowLabelType.None && this.props.rowLimit !== 1;
    const dropAreas = this.getEntryDropAreas(this.state.draggingOverStepIndex);
    const canDrop = !this.getEntries().length && dropAreas.length > 0;
    const isEmpty = this.getEntries(true).length === 0;
    const typeItems = Object.values(this.getAddListTypeItems());
    const numEntries = this.getEntries(true).length;
    const onAddTypeSelectMenuChange = (ev: CustomEvent) => {
      this.setState({
        isTypeSelectOpen: ev.detail.isOpen
      });
      this.dispatchTrack(QueryEntrySectionTrackingEvent.OpenEntryTypeSelect, {});
    };
    const addEntryButtonTooltipAttrs = (() => {
      if (this.props.disableAddEntryButtonTooltipText) {
        return null;
      }
      if (this.props.disableAddEntryReason) {
        return _objectSpread({
          text: this.props.disableAddEntryReason
        }, commonAddEntryTooltipAttrs);
      }
      if (this.isAddingDisabled()) {
        return null;
      }
      if (this.props.addEntryButtonTooltipText) {
        return _objectSpread({
          text: this.props.addEntryButtonTooltipText
        }, commonAddEntryTooltipAttrs);
      }
      const type = Object.values(this.getAddListTypeItems())[0].value;
      const tutorialTooltip = TutorialTooltips[type];
      if (tutorialTooltip) {
        return _objectSpread(_objectSpread({}, tutorialTooltip), {}, {
          text: print(tutorialTooltip.text)
        });
      }
      return null;
    })();
    return <div ref={this.rootEl} elref={this.props.elref} className={clsx("mp-query-entry-section", {
      'has-labels': showLabels,
      'is-empty': isEmpty,
      'is-draggable': this.props.rowDraggable,
      'for-new-metrics-mode': true,
      'for-new-ui': hasNewUI,
      'for-list-objects': !!this.props.listProperty,
      'behavior-mode': this.props.behaviorMode,
      'contained-section': this.props.containedSection,
      'indent-filters': this.props.indentFilters
    }, this.props.className)} onClick={this.props.onClick} onKeyUp={this.props.onKeyUp} data-sentry-component="QueryEntrySection" data-sentry-source-file="index.tsx">
        {this.props.measurementBlock}
        {hasStickyHeader && <StickyPlaceholderReact stickyElm={this.headerEl.current} />}
        {hasNewHeader && <AddEntryHeader headerRef={this.headerEl} label={this.props.sectionTitle} isAddingDisabled={this.isAddingDisabled()} reportName={this.props.reportName} disableAddEntry={this.props.disableAddEntry}
      // If there is only one entry that is an empty placeholder (intial empty state), do not disable select.
      isSelectDisabled={false} rowLimit={this.props.rowLimit} canDrop={dropAreas.includes(DROP_POSITION.HEADER)} canAlterEntries={!this.props.readonly && (this.isDuplicateSupported() || this.canDeleteEntry())} addEntryMenuWidth={this.props.addEntryMenuWidth} numEntries={numEntries} isStickyHeader={hasStickyHeader} isTypeSelectOpen={this.state.isTypeSelectOpen} draggingOverStepIndex={this.state.draggingOverStepIndex} typeItems={typeItems} tooltipAttrs={addEntryButtonTooltipAttrs} onAddTypeSelectMenuChange={onAddTypeSelectMenuChange} onClickAdd={(type, typeSelect) => this.addEntryRow(type, {
        typeSelect
      })} onDragLeave={() => this.setState({
        draggingOverStepIndex: null
      })} onDrop={ev => this.onEntryStepDrop(ev, 0)} onDragOver={ev => this.onEntryStepDragOver(ev, 0)} headerItems={this.props.headerItems} />}
        <div className='mp-query-entry-section-body'>
          {this.getEntries(true).map((entry, index) => {
          const Component = getEntryRowHelpers(entry).component;
          const props = this.getEntryProps(entry, index);
          return <Component key={entry.entryKey} {...props} data-sentry-element="Component" data-sentry-component="QueryEntrySection" data-sentry-source-file="index.tsx" />;
        })}
        </div>
        {displayFooter && <div className={clsx("mp-query-entry-section-footer", {
        'is-empty': isEmpty
      })}>
            {showAddButton && <div className={clsx("mp-query-add-entry-row", {
          'drop-indicator-above': this.state.draggingOverStepIndex === this.getEntries().length && canDrop
        })} onDragLeave={() => this.setState({
          draggingOverStepIndex: null
        })} onDrop={ev => canDrop && this.onEntryStepDrop(ev, this.getEntries().length)} onDragOver={ev => this.onEntryStepDragOver(ev, this.getEntries().length)}>
                <AddEntryRow hasContainedUI={this.hasNewQBUI() && !this.props.containedSection} hasPrefixRenderer={!!this.props.renderPrefix} isDisabled={this.isAddingDisabled()} typeItems={typeItems} tooltipAttrs={addEntryButtonTooltipAttrs} addEntryButtonLabel={this.props.addEntryButtonLabel} addEntryMenuWidth={this.props.addEntryMenuWidth} isOpen={this.state.isTypeSelectOpen} autofocus={this.props.autofocus} onAddEntryRow={type => this.addEntryRow(type)} onAddTypeSelectMenuChange={onAddTypeSelectMenuChange} />
              </div>}

            {this.props.footerItems}
          </div>}
      </div>;
  }
}
_defineProperty(QueryEntrySection, "defaultProps", {
  addEntryMenuWidth: 110,
  groupEventBehavior: GroupEventBehaviors.None,
  dateUnit: RollingWindowSize.Day,
  propertyCombineMode: null,
  disabledItems: {},
  entryType: EntryType.Event,
  rowLabelTypeForExclusionSteps: RowLabelType.Numeric,
  entries: [],
  includeCustomProperties: IncludeCustomPropertyType.None,
  rowTypes: [ResourceType.Event],
  rowIndex: null,
  rowLabelType: RowLabelType.Numeric,
  timeDisplayContext: DisplayContext.FullLength,
  trackProperties: {},
  projectAsOfDate: "",
  eventNames: null,
  otherGroupByOptions: [],
  otherFilterOptions: [],
  otherAggregationOptions: [],
  minEntries: 0
});
export const QueryEntrySectionReact = withStyles((props: QueryEntrySectionProps) => {
  const queryContextManager = useQueryContextManager();
  const metrics = useMetrics();
  const behaviors = useBehaviors();
  const currentProject = useCurrentProject();
  const dragAndDropManager = useDragAndDropManager();
  const i18n = useTranslation();
  return <QueryEntrySection queryContextManager={queryContextManager} metrics={metrics} behaviors={behaviors} currentProject={currentProject} dragAndDropManager={dragAndDropManager} i18n={i18n} {...props} />;
}, css);